/**
 * mentorship.ts — Zod schemas mirroring the backend mentorship serializers.
 *
 * Purpose: defensive runtime validation of /api/(education/)mentorship/*
 * payloads. Why Zod here and not elsewhere yet?
 *   - The hand-rolled TypeScript types in `mentorshipService.ts` are
 *     STRUCTURAL only: they're erased at runtime, so a backend contract
 *     drift (renamed field, wrong type) silently produces an `undefined`
 *     that explodes three components downstream with no traceable origin.
 *   - For the Tier 2 sub-slice 3 finalize endpoint (which is now a real
 *     write path with idempotency + ETag semantics), the cost of a
 *     malformed-response crash is much higher than the few KB of Zod.
 *
 * Boundary discipline:
 *   - Parse at the service boundary (mentorshipService.finalizeMentorReview
 *     calls ReportSchema.parse on response.data).
 *   - Do NOT re-parse inside components — once a value crosses into the
 *     React layer it's trusted.
 *   - Schemas are `.strict()` BY DEFAULT (ADR-0002 §2.2 — fail-fast on
 *     contract drift). Required scalars trip on missing/wrong-typed
 *     fields; UNKNOWN keys ALSO trip the parse. If a specific endpoint
 *     genuinely needs forward-compat tolerance, opt that endpoint into
 *     `.passthrough()` (or `.strip()`) with an inline `// passthrough —
 *     <why>. ADR-0002 §2.2.` rationale. The default is strict so a
 *     serializer typo or removed column surfaces at the boundary with a
 *     structured telemetry event instead of an `undefined` crash three
 *     components downstream.
 *
 * Naming:
 *   - The inferred `Report` and `Submission` types here mirror the
 *     structural `Report` / `Submission` types declared in
 *     mentorshipService.ts (the FE structural SSOT). Each new schema
 *     field maps 1:1 to a field on the wire; nullable / optional
 *     fields stay tolerant of absence for forward-compat.
 */

import { z } from 'zod';

/**
 * Submission state — FE source of truth in mentorshipService.ts is
 *   'submitted' | 'assessed' | 'reviewed' | 'finalized'
 * Backend can grow this (e.g. the five-dispute-state migration). We
 * stay conservative: any non-empty string is accepted; the component
 * branches on the literal it knows. Documenting the *expected* set in
 * a JSDoc comment is enough — enforcing it as an enum would force a
 * Zod parse failure the moment the backend adds a new state.
 */
export const SubmissionStateSchema = z.string().min(1);

/**
 * UserMiniSchema — the inlined mini-user projection emitted by every
 * serializer that surfaces a denormalised user (Submission, Meeting,
 * ReportDispute). Required scalars strict; the four fields are always
 * present on the wire even when the FK target is a soft-deleted user
 * (backend serializer falls back to empty strings).
 *
 * Hoisted above SubmissionSchema so the nested `mentee_summary` field
 * type resolves at module-evaluation time without a forward reference.
 */
export const UserMiniSchema = z.object({
    id: z.number().int(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
}).strict();

/**
 * ReportSubmissionSummarySchema — minimal Submission projection inlined
 * on Report so the mentee's ReportViewer can render "Module, Quarter"
 * context without a second round-trip. Mirrors
 * `ReportSubmissionSummarySerializer` on the backend.
 */
export const ReportSubmissionSummarySchema = z.object({
    id: z.string().uuid(),
    module: z.string().min(1),
    quarter: z.string().min(1),
    submitted_at: z.string(),
}).strict();

export const SubmissionSchema = z.object({
    id: z.string().uuid(),
    mentee: z.number().int().nonnegative(),
    mentee_summary: UserMiniSchema.nullable(),
    assigned_mentor: z.number().int().nullable(),
    module: z.string().min(1),
    quarter: z.string().min(1),
    content: z.string(),
    state: SubmissionStateSchema,
    is_locked: z.boolean(),
    client_submission_id: z.string(),
    assessment_id: z.string().uuid().nullable(),
    submitted_at: z.string(),
    updated_at: z.string(),
    // Forward-compat: denormalised review fields some serializers
    // inline on the submission row (mentor_feedback / mentor_score)
    // and the legacy client_review_id projection that older clients
    // still receive. Tolerated so a serializer cutover doesn't trip
    // the boundary parse before the FE catches up.
    mentor_feedback: z.string().nullable().optional(),
    mentor_score: z.number().int().nonnegative().nullable().optional(),
    client_review_id: z.string().nullable().optional(),
    // Optimistic-concurrency counter exposed by the BE serializer
    // (Weller100 education/serializers.py:151 — read-only Meta field
    // on SubmissionSerializer). The FE echoes it back as the
    // `If-Match` precondition header on finalize / patch writes so
    // concurrent edits surface as 412 (handled by the existing
    // submissionETag/ifMatch plumbing in mentorshipService). The
    // boundary parse rejected this field before the FE caught up
    // (Zod strict mode); allowlisting closes the contract drift.
    // Optional + nullable mirrors the forward-compat shape used by
    // adjacent denormalised fields above.
    version: z.number().int().nonnegative().nullable().optional(),
}).strict();

/**
 * ReportSchema — mirrors the backend ReportSerializer fields for the
 * finalize endpoint. Required scalars are strict; nullable/optional
 * fields are tolerant of absence (forward-compat).
 *
 * NOTE on `agreed_level`: backend `agreed_level` is a free-string field
 * across releases (FE has used both `Low|Mid|Senior` and `L1..L5` shapes
 * in different surfaces). We accept any non-empty string here; consumer
 * components branch on the literal they expect.
 */
export const ReportSchema = z.object({
    id: z.string().uuid(),
    assessment: z.string().uuid(),
    submission: z.string().uuid(),
    submission_summary: ReportSubmissionSummarySchema,
    mentee: z.number().int().nonnegative(),
    version: z.number().int().nonnegative(),
    final_score: z.number().int().nonnegative(),
    agreed_level: z.string().min(1),
    strengths: z.array(z.string()),
    gaps: z.array(z.string()),
    actions: z.array(z.string()),
    mentor_notes: z.string(),
    override_reason: z.string(),
    benchmark_tag: z.string(),
    finalized_by: z.number().int().nonnegative(),
    timestamp: z.string(),
    // Forward-compat: client_review_id is emitted by some Report
    // serializers (legacy mentor surface) but not all; the structural
    // Report type omits it, so we tolerate absence here. supersedes
    // links to the prior version when WS-M-1 rewrites a report row.
    client_review_id: z.string().uuid().nullable().optional(),
    supersedes: z.string().uuid().nullable().optional(),
}).strict();

export type FinalizedReport = z.infer<typeof ReportSchema>;
export type SubmissionLike = z.infer<typeof SubmissionSchema>;

/**
 * PaginatedSchema — Zod factory for the HmacCursorPagination envelope.
 *
 * Backend contract (api/pagination.py HmacCursorPagination):
 *   { results: T[], next_cursor: string | null, count?: number, page_size: number }
 *
 * `page_size` is the canonical envelope shape — the BE pagination
 * class emits it on every page so the FE knows the slice size without
 * having to count items. Earlier scaffolding omitted the field and
 * .strict() rejected production responses as ResponseShapeError
 * (every cursor-paged endpoint broke). The schema now declares it
 * explicitly; .strict() stays in force for the other 3 fields.
 *
 * `count` is optional because the backend may omit it on cheap pages
 * (it's COUNT(*) — the pagination class returns it on the first page
 * only). FE caches the first-page count and keeps showing it.
 *
 * `next_cursor` is an opaque HMAC-signed string; FE MUST NOT parse it.
 * Round-trip it verbatim to the next request.
 *
 * Usage:
 *   const PaginatedReports = PaginatedSchema(ReportSchema);
 *   const page = PaginatedReports.parse(response.data);
 */
export const PaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
    z.object({
        results: z.array(itemSchema),
        next_cursor: z.string().nullable(),
        count: z.number().int().nonnegative().optional(),
        page_size: z.number().int().positive(),
    }).strict();

/** Structural type of the paginated envelope.
 *
 *  Hand-rolled (not `z.infer`) because Zod's generic-factory inference
 *  is gnarly under exactOptionalPropertyTypes; the structural type is
 *  trivial and matches the schema by construction. */
export type PaginatedEnvelope<T> = {
    results: T[];
    next_cursor: string | null;
    count?: number;
    page_size: number;
};

/**
 * Tolerant Zod schemas for the remaining list-endpoint item shapes.
 *
 * Discipline: required scalars are strict (catch obvious drift);
 * everything else is `.optional()` or `.nullable()` (forward-compat;
 * the serializer growing a field doesn't crash the parse). The hand-
 * rolled TS types in `mentorshipService.ts` remain the structural
 * SSOT used by component code — these schemas are the runtime guard
 * at the boundary, not a re-derived type.
 */
export const InsightSchema = z.object({
    id: z.string().uuid(),
    user: z.number().int().nullable(),
    type: z.string().min(1),
    priority: z.string().min(1),
    // Tightened from .optional() → required per the structural Insight
    // TS type in mentorshipService.ts. The BE InsightSerializer always
    // emits these fields; before tightening, a consumer reading e.g.
    // `insight.trigger_context` would see `undefined` if the schema
    // silently let the field through — the TS contract said
    // non-null, but the runtime guard was lax. Bidirectional drift
    // now closed.
    trigger_context: z.string(),
    prompt: z.string(),
    source: z.string().min(1),
    is_active: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
    // source_report stays nullable+optional — FK to a Report row that
    // may be absent on insights minted from non-report triggers.
    source_report: z.string().nullable().optional(),
    // Counters stay optional — BE may omit on cheap list endpoints
    // and on freshly-minted insights before any feedback action.
    accepted_count: z.number().int().nonnegative().optional(),
    dismissed_count: z.number().int().nonnegative().optional(),
}).strict();

export const ClientProfileSchema = z.object({
    id: z.string().uuid(),
    client_id: z.string().min(1),
    priorities: z.array(z.string()).optional(),
    behaviors: z.array(z.string()).optional(),
    management_advice: z.array(z.string()).optional(),
    visibility: z.string().optional(),
    created_by: z.number().int().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
}).strict();

export const MentorshipProjectSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1),
    // Required per the structural MentorshipProject TS type in
    // mentorshipService.ts. Tightened from optional → required.min(1)
    // so consumers can rely on `project.client_id` without a guard.
    client_id: z.string().min(1),
    description: z.string().optional(),
    mentee_id: z.number().int(),
    mentor_id: z.number().int().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    is_active: z.boolean().optional(),
    created_by_id: z.number().int().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
    risks_count: z.number().int().nonnegative().optional(),
}).strict();

export const ReportDisputeSchema = z.object({
    id: z.string().uuid(),
    report: z.string().uuid(),
    mentee: z.number().int(),
    mentee_summary: UserMiniSchema.nullable(),
    reason: z.string().optional(),
    state: z.string().min(1),
    resolved_by: z.number().int().nullable().optional(),
    resolution_note: z.string().optional(),
    created_at: z.string().optional(),
    resolved_at: z.string().nullable().optional(),
}).strict();

// ─── Phase 1 Tier B schemas ─────────────────────────────────────────────
//
// New schemas authored for high-traffic READ endpoints. Discipline per
// ADR-0002 §2.2: `.strict()` by default; required scalars strict;
// optional/forward-compat fields tolerant (`.optional()` / `.nullable()`).
// The hand-rolled TS types in `mentorshipService.ts` remain the
// structural SSOT for component code — these schemas are runtime guards
// at the service boundary.

/**
 * AssessmentJustificationSchema — mirrors `AssessmentJustification` on
 * the AI assessment payload. All three array fields are required (the
 * backend always emits empty arrays rather than omitting), but each
 * entry is just a non-empty string.
 */
export const AssessmentJustificationSchema = z.object({
    strengths: z.array(z.string()),
    gaps: z.array(z.string()),
    incorrect_assumptions: z.array(z.string()),
}).strict();

/**
 * AssessmentSignalsSchema — keyword/concept signals + the
 * `thinking_patterns` flag pair. `thinking_patterns` is a strict object
 * because the FE branches on the two booleans directly.
 */
export const AssessmentSignalsSchema = z.object({
    keywords: z.array(z.string()),
    missing_concepts: z.array(z.string()),
    risk_awareness: z.array(z.string()),
    thinking_patterns: z.object({
        proactive: z.boolean(),
        if_then: z.boolean(),
    }).strict(),
}).strict();

/**
 * AssessmentSchema — the AI-assessment row read by `getAssessment`.
 * `state` is a free string (backend may grow the state set; FE branches
 * on the literal it knows). `score_ai` + `confidence` + `latency_ms` are
 * nullable while the assessment is still `pending_ai`.
 */
export const AssessmentSchema = z.object({
    id: z.string().uuid(),
    submission: z.string().uuid(),
    module: z.string().min(1),
    state: z.string().min(1),
    score_ai: z.number().nullable(),
    confidence: z.number().nullable(),
    justification: AssessmentJustificationSchema,
    signals: AssessmentSignalsSchema,
    flags: z.array(z.string()),
    mentor_prompts: z.array(z.string()),
    model_name: z.string(),
    prompt_version: z.string(),
    rubric_version: z.string(),
    error_detail: z.string(),
    latency_ms: z.number().nullable(),
    token_usage: z.record(z.string(), z.number()),
    created_at: z.string(),
    updated_at: z.string(),
    // Forward-compat for WS-M-1 ETag / multi-tenant boundary growth
    // on the AssessmentSerializer. The BE may add `version` (optimistic
    // concurrency, mirrors ReportSchema.version) and `organisation_id`
    // (tenant boundary) on either of these resources; declaring them
    // optional keeps the parse tolerant when those fields land.
    version: z.number().int().nonnegative().optional(),
    organisation_id: z.number().int().optional(),
}).strict();

/**
 * AssessmentReviewSchema — the synthesised mentor-prep row read by
 * `getAssessmentReview`. `suggested_score_range` is a 2-tuple of numbers
 * (min, max); we enforce the length so a drift to a single-number scalar
 * surfaces here instead of crashing the render.
 */
export const AssessmentReviewSchema = z.object({
    id: z.string().uuid(),
    assessment: z.string().uuid(),
    suggested_score_range: z.tuple([z.number(), z.number()]),
    level_mapping: z.string().min(1),
    review_focus: z.array(z.string()),
    confidence: z.number(),
    model_name: z.string(),
    prompt_version: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    // Forward-compat — see AssessmentSchema rationale above.
    version: z.number().int().nonnegative().optional(),
    organisation_id: z.number().int().optional(),
}).strict();

/**
 * FeedbackTemplateSchema — quick-options + focus-areas blob the mentor
 * UI loads ahead of a finalize. `editable` is the gate on whether the
 * UI shows the inline editor; defaults to true server-side.
 */
export const FeedbackTemplateSchema = z.object({
    id: z.string().uuid(),
    assessment: z.string().uuid(),
    quick_options: z.array(z.string()),
    focus_areas: z.array(z.string()),
    editable: z.boolean(),
    prompt_version: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
    // Forward-compat — see AssessmentSchema rationale above.
    version: z.number().int().nonnegative().optional(),
    organisation_id: z.number().int().optional(),
}).strict();

/**
 * RiskSchema — mirrors `Risk` in mentorshipService.ts (Risks live under
 * a MentorshipProject). `priority` is a non-empty string (backend may
 * grow the set beyond low/medium/high; FE consumer branches on the
 * literal it knows).
 */
export const RiskSchema = z.object({
    id: z.string().uuid(),
    project: z.string().uuid(),
    trigger: z.string(),
    impact: z.string(),
    mitigation: z.string(),
    priority: z.string().min(1),
    is_active: z.boolean(),
    created_by: z.number().int().nonnegative(),
    created_at: z.string(),
    updated_at: z.string(),
    // Forward-compat — Risks share the WS-M-1 ETag + multi-tenant
    // boundary posture with Reports. See AssessmentSchema rationale.
    version: z.number().int().nonnegative().optional(),
    organisation_id: z.number().int().optional(),
}).strict();

/**
 * ConcernHistoryEntrySchema — one row of the bidirectional Concern
 * timeline. `state` and `actor` are free strings (the WS-M-3 state
 * machine grows the set behind a feature flag; the schema stays
 * tolerant so a backend cutover doesn't crash the parse).
 */
export const ConcernHistoryEntrySchema = z.object({
    id: z.string().uuid(),
    dispute: z.string().uuid(),
    state: z.string().min(1),
    actor: z.string().min(1),
    actor_id: z.number().int().nullable(),
    actor_label: z.string(),
    comment: z.string(),
    timestamp: z.string(),
}).strict();

// ─── Phase 1 mutation-side sweep schemas (Tier D writes) ───────────────
//
// New schemas authored to wrap the highest-impact WRITE responses with
// `parseAtBoundary`. Same posture as Tier B reads: `.strict()` per
// ADR-0002 §2.2; required scalars strict; optional/forward-compat fields
// `.optional()` / `.nullable()`. The hand-rolled TS types in
// `mentorshipService.ts` remain the structural SSOT — these schemas are
// runtime guards at the boundary, not a re-derived type.

/**
 * MeetingHistoryEntrySchema — one row of the meeting's audit-trail
 * history. `action` is a free string (the backend may grow the verb
 * set; FE branches on the literal it knows).
 */
export const MeetingHistoryEntrySchema = z.object({
    at: z.string(),
    actor_id: z.number().int(),
    action: z.string().min(1),
    reason: z.string(),
}).strict();

/**
 * MeetingSchema — mirrors `Meeting` in mentorshipService.ts. `cadence`
 * and `status` are free strings (`.min(1)`) rather than `z.enum(...)`
 * so a backend that grows the state set (WS-M-4 / WS-ENT-2 may add
 * `rescheduled`, etc.) does not crash the parse mid-flight. FE
 * consumers branch on the literal they know.
 */
export const MeetingSchema = z.object({
    id: z.string().uuid(),
    mentor: z.number().int(),
    mentee: z.number().int(),
    mentor_summary: UserMiniSchema.nullable(),
    mentee_summary: UserMiniSchema.nullable(),
    next_meeting_at: z.string(),
    cadence: z.string().min(1),
    status: z.string().min(1),
    duration_minutes: z.number().int().nonnegative(),
    location: z.string(),
    notes: z.string(),
    history: z.array(MeetingHistoryEntrySchema),
    created_at: z.string(),
    updated_at: z.string(),
}).strict();

/**
 * PairingMentorSchema — minimal mentor projection inlined on the
 * pairing-status response. Two required scalars; `.strict()` so a
 * serializer growing a third field surfaces here instead of leaking
 * an unknown key past the boundary into the hook contract.
 *
 * NOTE: this mirrors the FE-facing `PairingMentor` shape declared in
 * `modules/mentorship/pairing/usePairingStatus.ts` — kept in lockstep
 * by construction (id + name), not via a re-derived type.
 */
export const PairingMentorSchema = z.object({
    id: z.number().int().nonnegative(),
    name: z.string().min(1),
}).strict();

/**
 * PairingStatusSchema — mirrors GET /api/mentorship/pairing/status/
 * (BE commit 8bf52b52). Wire shape:
 *
 *   { stage: 1|2|3|4, queuePosition?: number, mentor?: { id, name } }
 *
 * Backend OMITS the optional fields rather than emitting `null` — the
 * schema mirrors that posture: `queuePosition` and `mentor` are bare
 * `.optional()` (no `.nullable()`) so the inferred type composes
 * cleanly under `exactOptionalPropertyTypes`. A `null` on the wire
 * would fail the parse here and surface as `ResponseShapeError`,
 * which is the desired failure mode — a contract drift between BE's
 * "omit when absent" and FE's optional-only typing should be loud,
 * not silently coerced into an empty render.
 *
 * `stage` is a hard enum (`z.union([z.literal(1), …])`) rather than
 * a free `z.number()` — the FE branches exhaustively on the four
 * values in `PairingCard`'s `STAGE_DEFS` + `describeStage`. A backend
 * that grows a stage 5 SHOULD trip this parse (the FE has no copy
 * for an unknown stage); the captureError telemetry then drives the
 * follow-up sweep.
 *
 * `.strict()` per ADR-0002 §2.2 — unknown keys fail the parse so a
 * serializer typo / removed column surfaces with a structured event.
 */
export const PairingStatusSchema = z.object({
    stage: z.union([
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
    ]),
    queuePosition: z.number().int().nonnegative().optional(),
    mentor: PairingMentorSchema.optional(),
}).strict();

/** Inferred type — wire shape only (no hook-level `loading` / `error`
 *  / `retry` plumbing). The hook composes this with its own state
 *  machine; the schema-derived type is the on-wire contract. */
export type PairingStatusWire = z.infer<typeof PairingStatusSchema>;

/**
 * FinalizeMentorReviewResultSchema — composed shape returned by
 * `finalizeMentorReview`: the report row plus the FE-added `created`
 * flag and surfaced `submissionETag` header value.
 *
 * NOTE: only the `report` field is parsed at the service boundary
 * (the other two fields are derived FE-side from HTTP response.status
 * and response.headers — there's no wire shape to validate). This
 * schema is exposed so the contract is documented in ONE place; the
 * service module wraps `response.data` against `ReportSchema` (the
 * load-bearing half of the response payload) per the same posture as
 * the legacy `.parse()` site.
 */
export const FinalizeMentorReviewResultSchema = z.object({
    report: ReportSchema,
    submissionETag: z.string().nullable(),
    created: z.boolean(),
}).strict();
